/**
 * BookPlanView - Book arc planning interface
 *
 * Allows the user to:
 * 1. Request an LLM-proposed book structure from stack cards
 * 2. Edit chapter titles, drag cards between chapters, add/remove chapters
 * 3. Apply the plan to create a Book + N Chapters with card assignments
 *
 * @module @humanizer/studio/components/workspace/BookPlanView
 */

import { useState, useCallback } from 'react';
import { useBufferSync, cleanBufferName } from '../../contexts/BufferSyncContext';
import { useBook } from '../../contexts/BookContext';
import { useToast } from '../../contexts/ToastContext';
import type { BookData, BookPlanChapter, BookPlanResult } from '../../types/electron';
import { createLogger } from '../../utils/debug';
import '../../styles/book-plan-view.css';

const log = createLogger('BookPlanView');

const ARC_TYPES = [
  { id: 'chronological', label: 'Chronological', desc: 'Ordered by time' },
  { id: 'thematic', label: 'Thematic', desc: 'Grouped by theme' },
  { id: 'argumentative', label: 'Argumentative', desc: 'Building a case' },
  { id: 'narrative', label: 'Narrative', desc: 'Story arc' },
] as const;

interface BookPlanViewProps {
  onPlanApplied: () => void;
}

export function BookPlanView({ onPlanApplied }: BookPlanViewProps) {
  const { stackItems } = useBufferSync();
  const { planAndCreateBook, addChaptersToBook, stashBook, activeBook, setActiveBook } = useBook();
  const { addToast } = useToast();

  const [isGenerating, setIsGenerating] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [plan, setPlan] = useState<BookPlanResult | null>(null);
  const [editableTitle, setEditableTitle] = useState('');
  const [editableSubtitle, setEditableSubtitle] = useState('');
  const [editableChapters, setEditableChapters] = useState<BookPlanChapter[]>([]);
  const [selectedArc, setSelectedArc] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  // Drag state for cards between chapters
  const [dragCardIndex, setDragCardIndex] = useState<number | null>(null);
  const [dragFromChapter, setDragFromChapter] = useState<number | null>(null);
  const [dragOverChapter, setDragOverChapter] = useState<number | null>(null);

  const cardTitles = stackItems.map(item =>
    cleanBufferName(item.contentItem?.metadata?.source?.threadTitle as string
      ?? item.contentItem?.metadata?.source?.path?.[0] as string
      ?? item.contentItem?.text?.slice(0, 60) ?? `Card ${item.bufferId.slice(0, 6)}`)
  );

  const handleGenerate = useCallback(async () => {
    if (!window.electronBuffer?.planBook) {
      setError('Book planning requires the desktop app');
      return;
    }
    setIsGenerating(true);
    setError(null);
    try {
      const result = await window.electronBuffer.planBook({
        cardTexts: stackItems.map(item => item.contentItem.text.slice(0, 2000)),
        cardTitles,
        arcPreference: selectedArc || undefined,
      });
      setPlan(result);
      setEditableTitle(result.title);
      setEditableSubtitle(result.subtitle);
      setEditableChapters(result.chapters);
      if (!selectedArc) setSelectedArc(result.arcType);
      log.info('[BookPlanView] Plan generated:', result.chapters.length, 'chapters');
    } catch (err) {
      log.error('[BookPlanView] Generation failed:', err);
      setError('Failed to generate book plan. Try again.');
    } finally {
      setIsGenerating(false);
    }
  }, [stackItems, cardTitles, selectedArc]);

  const handleApply = useCallback(async () => {
    if (editableChapters.length === 0) return;
    setIsApplying(true);
    try {
      const cardBufferIds = stackItems.map(item => item.bufferId);
      let result: BookData | null;

      if (activeBook && (activeBook.chapters?.length ?? 0) === 0) {
        // Empty active book — add chapters to it
        result = await addChaptersToBook(activeBook.id, {
          chapters: editableChapters,
          cardBufferIds,
        });
      } else {
        // No active book — create new book with chapters
        result = await planAndCreateBook({
          title: editableTitle || 'Untitled',
          description: editableSubtitle,
          chapters: editableChapters,
          cardBufferIds,
        });
      }

      if (result) {
        addToast(`Book created: ${result.chapters.length} chapters`, 'success');
        onPlanApplied();
      } else {
        addToast('Failed to create book', 'warning');
      }
    } catch (err) {
      log.error('[BookPlanView] Apply failed:', err);
      addToast('Failed to create book', 'warning');
    } finally {
      setIsApplying(false);
    }
  }, [editableTitle, editableSubtitle, editableChapters, stackItems, activeBook, planAndCreateBook, addChaptersToBook, addToast, onPlanApplied]);

  const handleChapterTitleChange = useCallback((idx: number, title: string) => {
    setEditableChapters(prev => prev.map((ch, i) => i === idx ? { ...ch, title } : ch));
  }, []);

  const handleAddChapter = useCallback(() => {
    setEditableChapters(prev => [...prev, {
      title: `Chapter ${prev.length + 1}`,
      theme: '',
      summary: '',
      cardIndices: [],
    }]);
  }, []);

  const handleRemoveChapter = useCallback((idx: number) => {
    setEditableChapters(prev => prev.filter((_, i) => i !== idx));
  }, []);

  // Drag handlers for moving cards between chapters
  const handleCardDragStart = useCallback((cardIndex: number, chapterIdx: number) => {
    setDragCardIndex(cardIndex);
    setDragFromChapter(chapterIdx);
  }, []);

  const handleChapterDragOver = useCallback((e: React.DragEvent, chapterIdx: number) => {
    e.preventDefault();
    setDragOverChapter(chapterIdx);
  }, []);

  const handleChapterDrop = useCallback((e: React.DragEvent, toChapterIdx: number) => {
    e.preventDefault();
    if (dragCardIndex === null || dragFromChapter === null) return;

    setEditableChapters(prev => {
      const updated = prev.map((ch, i) => {
        if (i === dragFromChapter) {
          // Remove card from source chapter
          return { ...ch, cardIndices: ch.cardIndices.filter(ci => ci !== dragCardIndex) };
        }
        if (i === toChapterIdx) {
          // Add card to target chapter (avoid duplicates)
          if (ch.cardIndices.includes(dragCardIndex!)) return ch;
          return { ...ch, cardIndices: [...ch.cardIndices, dragCardIndex!] };
        }
        return ch;
      });
      return updated;
    });

    setDragCardIndex(null);
    setDragFromChapter(null);
    setDragOverChapter(null);
  }, [dragCardIndex, dragFromChapter]);

  const handleRemoveCardFromChapter = useCallback((chapterIdx: number, cardIndex: number) => {
    setEditableChapters(prev => prev.map((ch, i) =>
      i === chapterIdx ? { ...ch, cardIndices: ch.cardIndices.filter(ci => ci !== cardIndex) } : ch
    ));
  }, []);

  // Keyboard: move card to adjacent chapter (Ctrl+Arrow)
  const handleCardKeyboardMove = useCallback((cardIdx: number, fromChapterIdx: number, direction: 'left' | 'right') => {
    const toChapterIdx = direction === 'left' ? fromChapterIdx - 1 : fromChapterIdx + 1;
    setEditableChapters(prev => {
      if (toChapterIdx < 0 || toChapterIdx >= prev.length) return prev;
      return prev.map((ch, i) => {
        if (i === fromChapterIdx) {
          return { ...ch, cardIndices: ch.cardIndices.filter(ci => ci !== cardIdx) };
        }
        if (i === toChapterIdx) {
          if (ch.cardIndices.includes(cardIdx)) return ch;
          return { ...ch, cardIndices: [...ch.cardIndices, cardIdx] };
        }
        return ch;
      });
    });
  }, []);

  // Keyboard: assign unassigned card to first chapter
  const handleAssignUnassignedCard = useCallback((cardIdx: number) => {
    setEditableChapters(prev => {
      if (prev.length === 0) return prev;
      const target = prev[0];
      if (target.cardIndices.includes(cardIdx)) return prev;
      return prev.map((ch, i) =>
        i === 0 ? { ...ch, cardIndices: [...ch.cardIndices, cardIdx] } : ch
      );
    });
  }, []);

  // Unassigned cards (not in any chapter)
  const assignedCards = new Set(editableChapters.flatMap(ch => ch.cardIndices));
  const unassignedCards = stackItems.map((_, i) => i).filter(i => !assignedCards.has(i));

  const [isStashing, setIsStashing] = useState(false);

  const handleStash = useCallback(async () => {
    if (!activeBook) return;
    setIsStashing(true);
    try {
      const newBook = await stashBook(activeBook.id);
      if (newBook) {
        addToast(`"${activeBook.title}" stashed. Planning fresh book.`, 'success');
      }
    } catch (err) {
      log.error('[BookPlanView] Stash failed:', err);
      addToast('Failed to stash book', 'warning');
    } finally {
      setIsStashing(false);
    }
  }, [activeBook, stashBook, addToast]);

  const handlePlanNew = useCallback(() => {
    setActiveBook(null);
  }, [setActiveBook]);

  // Active book with chapters — offer stash or new plan
  if (activeBook && (activeBook.chapters?.length ?? 0) > 0) {
    return (
      <div className="book-plan">
        <div className="book-plan__existing">
          <h3 className="book-plan__existing-title">Book: {activeBook.title}</h3>
          <p className="book-plan__existing-info">
            {activeBook.chapters!.length} chapter{activeBook.chapters!.length !== 1 ? 's' : ''}
          </p>
          <p className="book-plan__existing-desc">
            This book already has chapters. You can stash it and start fresh, or plan a completely new book.
          </p>
          <div className="book-plan__existing-actions">
            <button
              className="book-plan__action-btn book-plan__action-btn--primary"
              onClick={handleStash}
              disabled={isStashing}
            >
              {isStashing ? 'Stashing...' : 'Stash & Re-plan'}
            </button>
            <button
              className="book-plan__action-btn book-plan__action-btn--secondary"
              onClick={handlePlanNew}
            >
              Plan New Book
            </button>
          </div>
        </div>
      </div>
    );
  }
  // Active book with 0 chapters OR no active book — show plan UI (fall through)

  return (
    <div className="book-plan">
      {/* Pre-generation: arc selector + generate button */}
      {!plan && (
        <div className="book-plan__setup">
          <h3 className="book-plan__setup-title">Plan Your Book</h3>
          <p className="book-plan__setup-desc">
            The AI will analyze your {stackItems.length} cards and propose a chapter structure.
          </p>

          <div className="book-plan__arc-selector">
            <label className="book-plan__label">Arc preference (optional)</label>
            <div className="book-plan__arc-options">
              {ARC_TYPES.map(arc => (
                <button
                  key={arc.id}
                  className={`book-plan__arc-btn ${selectedArc === arc.id ? 'book-plan__arc-btn--active' : ''}`}
                  onClick={() => setSelectedArc(prev => prev === arc.id ? '' : arc.id)}
                >
                  <span className="book-plan__arc-btn-label">{arc.label}</span>
                  <span className="book-plan__arc-btn-desc">{arc.desc}</span>
                </button>
              ))}
            </div>
          </div>

          {error && <div className="book-plan__error" role="alert">{error}</div>}

          <button
            className="book-plan__generate-btn"
            onClick={handleGenerate}
            disabled={isGenerating || stackItems.length < 3}
          >
            {isGenerating ? 'Analyzing cards...' : 'Generate Plan'}
          </button>

          {stackItems.length < 3 && (
            <p className="book-plan__hint">Add at least 3 cards to plan a book.</p>
          )}
        </div>
      )}

      {/* Post-generation: editable plan */}
      {plan && (
        <div className="book-plan__editor">
          {/* Book metadata */}
          <div className="book-plan__meta">
            <input
              className="book-plan__title-input"
              value={editableTitle}
              onChange={e => setEditableTitle(e.target.value)}
              placeholder="Book title..."
            />
            <input
              className="book-plan__subtitle-input"
              value={editableSubtitle}
              onChange={e => setEditableSubtitle(e.target.value)}
              placeholder="Subtitle (optional)..."
            />
            <div className="book-plan__meta-info">
              <span className="book-plan__arc-badge">{selectedArc || plan.arcType}</span>
              {plan.model && <span className="book-plan__model-badge">{plan.model}</span>}
              {plan.generationTimeMs && (
                <span className="book-plan__time-badge">
                  {(plan.generationTimeMs / 1000).toFixed(1)}s
                </span>
              )}
            </div>
          </div>

          {/* Chapters */}
          <div className="book-plan__chapters">
            <div className="book-plan__chapters-header">
              <h4 className="book-plan__chapters-label">
                {editableChapters.length} Chapters
              </h4>
              <button className="book-plan__add-chapter-btn" onClick={handleAddChapter}>
                + Chapter
              </button>
            </div>

            {editableChapters.map((chapter, chIdx) => (
              <div
                key={chIdx}
                className={`book-plan__chapter ${dragOverChapter === chIdx ? 'book-plan__chapter--drag-over' : ''}`}
                onDragOver={e => handleChapterDragOver(e, chIdx)}
                onDrop={e => handleChapterDrop(e, chIdx)}
              >
                <div className="book-plan__chapter-header">
                  <span className="book-plan__chapter-number">{chIdx + 1}</span>
                  <input
                    className="book-plan__chapter-title-input"
                    value={chapter.title}
                    onChange={e => handleChapterTitleChange(chIdx, e.target.value)}
                    placeholder="Chapter title..."
                  />
                  <button
                    className="book-plan__chapter-remove"
                    onClick={() => handleRemoveChapter(chIdx)}
                    title="Remove chapter"
                    aria-label="Remove chapter"
                  >
                    &times;
                  </button>
                </div>

                {chapter.summary && (
                  <p className="book-plan__chapter-summary">{chapter.summary}</p>
                )}

                <div className="book-plan__chapter-cards">
                  {chapter.cardIndices.map(cardIdx => (
                    <button
                      key={cardIdx}
                      className="book-plan__card-pill"
                      draggable
                      onDragStart={() => handleCardDragStart(cardIdx, chIdx)}
                      onKeyDown={(e) => {
                        if (e.ctrlKey || e.metaKey) {
                          if (e.key === 'ArrowLeft' && chIdx > 0) {
                            e.preventDefault();
                            handleCardKeyboardMove(cardIdx, chIdx, 'left');
                          } else if (e.key === 'ArrowRight' && chIdx < editableChapters.length - 1) {
                            e.preventDefault();
                            handleCardKeyboardMove(cardIdx, chIdx, 'right');
                          }
                        }
                        if (e.key === 'Delete' || e.key === 'Backspace') {
                          e.preventDefault();
                          handleRemoveCardFromChapter(chIdx, cardIdx);
                        }
                      }}
                      aria-label={`Card: ${cardTitles[cardIdx] ?? `Card ${cardIdx + 1}`}. Ctrl+Arrow to move between chapters, Delete to remove`}
                    >
                      {cardTitles[cardIdx] ?? `Card ${cardIdx + 1}`}
                      <span
                        role="button"
                        tabIndex={-1}
                        className="book-plan__card-pill-remove"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveCardFromChapter(chIdx, cardIdx);
                        }}
                        aria-label={`Remove card ${cardIdx + 1}`}
                      >
                        &times;
                      </span>
                    </button>
                  ))}
                  {chapter.cardIndices.length === 0 && (
                    <span className="book-plan__chapter-empty">
                      Drag cards here or use Ctrl+Arrow on unassigned cards
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Unassigned cards */}
          {unassignedCards.length > 0 && (
            <div className="book-plan__unassigned">
              <h4 className="book-plan__unassigned-label">
                Unassigned ({unassignedCards.length})
              </h4>
              <div className="book-plan__unassigned-cards">
                {unassignedCards.map(cardIdx => (
                  <button
                    key={cardIdx}
                    className="book-plan__card-pill book-plan__card-pill--unassigned"
                    draggable
                    onDragStart={() => {
                      setDragCardIndex(cardIdx);
                      setDragFromChapter(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleAssignUnassignedCard(cardIdx);
                      }
                    }}
                    aria-label={`Unassigned card: ${cardTitles[cardIdx] ?? `Card ${cardIdx + 1}`}. Press Enter to assign to first chapter, or drag`}
                  >
                    {cardTitles[cardIdx] ?? `Card ${cardIdx + 1}`}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="book-plan__actions">
            <button
              className="book-plan__action-btn book-plan__action-btn--secondary"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              {isGenerating ? 'Regenerating...' : 'Regenerate'}
            </button>
            <button
              className="book-plan__action-btn book-plan__action-btn--primary"
              onClick={handleApply}
              disabled={isApplying || editableChapters.length === 0}
            >
              {isApplying ? 'Creating...' : 'Apply Plan'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
